/**
 * 文档寻址与遍历 —— 编辑器对真实 SVG DOM 的读层（只读，永不改）。
 *
 * 寻址用「根 → 节点的元素子节点索引链」而不是 id：
 * 外部 SVG 常见 id 缺失/重复/结构变动后失效，索引链在 undo 回放时反而稳定。
 * 注意：索引只在**元素子节点**（`children`）里算，文本/注释节点不占位。
 */
export const SVG_NS = 'http://www.w3.org/2000/svg';

export type Path = readonly number[];

/** 容器/元数据类：不进子树 —— 它们内部的一切都不是可编辑对象（如 <defs> 里的 <stop>） */
const CONTAINER_TAGS = new Set([
  'defs',
  'style',
  'script',
  'metadata',
  'title',
  'desc',
  'symbol',
  'marker',
  'pattern',
  'lineargradient',
  'radialgradient',
  'filter',
  'clippath',
  'mask',
]);

/** 一律不可选中：容器 + 根 <svg> + 渐变色标 + 动画 */
const NON_EDITABLE = new Set([...CONTAINER_TAGS, 'svg', 'stop']);
/** 文本行内片段：属于父 <text>，不单独进图层列表（参考产品同样不列） */
const INLINE_TAGS = new Set(['tspan', 'textpath']);

const tag = (el: Element): string => el.tagName.toLowerCase();

const isContainer = (el: Element): boolean => CONTAINER_TAGS.has(tag(el));

/**
 * 该元素是否可以出现在图层列表里并被选中。
 *
 * ⚠️ 必须限定在 SVG 命名空间内：宿主 div / body / html 这些 HTML 标签
 * 标签名不在排除表里，只按标签名判断会把它们当成可编辑元素 ——
 * 于是"点空白画布"会选中 <html>（真踩过）。
 */
export const isEditable = (el: Element): boolean =>
  el.namespaceURI === SVG_NS &&
  !NON_EDITABLE.has(tag(el)) &&
  !INLINE_TAGS.has(tag(el)) &&
  !tag(el).startsWith('animate');

/** 从根到 el 的索引链；el 不在 root 子树内时返回 null */
export function pathOf(el: Element, root: Element): Path | null {
  const path: number[] = [];
  for (let cur: Element | null = el; cur && cur !== root; cur = cur.parentElement) {
    const parent: Element | null = cur.parentElement;
    if (!parent) return null;
    const idx = Array.prototype.indexOf.call(parent.children, cur);
    if (idx < 0) return null;
    path.unshift(idx);
  }
  return el === root || path.length > 0 ? path : null;
}

/** 索引链取回元素；越界/不存在时返回 null */
export function nodeAt(root: Element, path: Path): Element | null {
  let cur: Element | null = root;
  for (const idx of path) {
    cur = (cur.children[idx] as Element | undefined) ?? null;
    if (!cur) return null;
  }
  return cur;
}

/** 先序（文档序）遍历：根自身以空路径回调，随后是全部后代（含容器内部） */
export function walk(root: Element, fn: (el: Element, path: Path) => void): void {
  const visit = (el: Element, path: number[]): void => {
    fn(el, path);
    for (let i = 0; i < el.children.length; i++) {
      visit(el.children[i] as Element, [...path, i]);
    }
  };
  visit(root, []);
}

/** 遍历全部可编辑元素（图层列表的数据源）；整体跳过容器子树 */
export function walkEditable(root: Element, fn: (el: Element, path: Path) => void): void {
  const visit = (el: Element, path: number[]): void => {
    if (path.length > 0 && isEditable(el)) fn(el, path);
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i] as Element;
      if (!isContainer(child)) visit(child, [...path, i]);
    }
  };
  visit(root, []);
}
